import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { MultiSelect } from '../components/ui/MultiSelect';
import { Textarea } from '../components/ui/Textarea';
import { Tooltip } from '../components/ui/Tooltip';
import { DatePicker } from '../components/ui/DatePicker';
import { Plus, Filter, Download, Eye, Edit, Search, PhoneCall, User, Upload, Trash2, Ban, AlertTriangle, Save, CheckCircle2, X, CalendarClock, Calendar, Clock } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data
const initialLeadsData = Array.from({
  length: 50
}, (_, i) => ({
  id: String(i + 1),
  name: `Azienda Lead ${i} SPA`,
  contact: `Referente ${i}`,
  phone: `333 ${1000000 + i}`,
  email: `info@lead${i}.it`,
  status: i % 5 === 0 ? 'Positivo' : i % 5 === 1 ? 'Negativo' : i % 5 === 2 ? 'In lavorazione' : i % 5 === 3 ? 'Da ricontattare' : 'Da contattare',
  source: i % 4 === 0 ? 'Fiera' : i % 4 === 1 ? 'Web' : i % 4 === 2 ? 'Passaparola' : 'Altro',
  services: ['Consulenza'],
  notes: 'Interessati a sicurezza',
  lastContact: '2024-05-10',
  assignedTo: i % 3 === 0 ? 'Alessio' : i % 3 === 1 ? 'Lorenzo' : 'Gian Franco',
  referent: `Referente Interno ${i % 4}`,
  // New fields for specific statuses
  callbackDate: i % 5 === 3 ? '2024-06-15' : '',
  callbackTime: i % 5 === 3 ? '10:00' : '',
  callbackNote: i % 5 === 3 ? 'Richiamare per conferma' : '',
  appointmentDate: i % 5 === 2 ? '2024-06-20' : '',
  appointmentTime: i % 5 === 2 ? '14:30' : '',
  appointmentNote: i % 5 === 2 ? 'Incontro preliminare' : ''
}));
const blacklistData = [{
  id: '1',
  name: 'Spam Company SRL',
  phone: '0000000000',
  reason: 'Non interessato',
  date: '2024-01-01',
  by: 'Admin'
}];
const columnsConfig = [{
  key: 'name',
  label: 'Ragione sociale'
}, {
  key: 'contact',
  label: 'Referente'
}, {
  key: 'phone',
  label: 'Telefono'
}, {
  key: 'source',
  label: 'Provenienza'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'lastContact',
  label: 'Ultimo contatto'
}, {
  key: 'assignedTo',
  label: 'Assegnato a'
}, {
  key: 'actions',
  label: 'Azioni'
}];
const kanbanColumns = [{
  id: 'Da contattare',
  title: 'Da contattare',
  color: 'bg-slate-50 border-slate-200'
}, {
  id: 'Da ricontattare',
  title: 'Da ricontattare',
  color: 'bg-yellow-50 border-yellow-200'
}, {
  id: 'In lavorazione',
  title: 'In lavorazione',
  color: 'bg-blue-50 border-blue-200'
}, {
  id: 'Positivo',
  title: 'Positivo',
  color: 'bg-green-50 border-green-200'
}, {
  id: 'Negativo',
  title: 'Negativo',
  color: 'bg-red-50 border-red-200'
}];
export function CommercialPage() {
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'blacklist'>('kanban');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isBlacklistModalOpen, setIsBlacklistModalOpen] = useState(false);
  const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [leadsData, setLeadsData] = useState(initialLeadsData);
  const [draggedCard, setDraggedCard] = useState<any>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [callbackSaveSuccess, setCallbackSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(leadsData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['name', 'contact', 'phone']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({
      status: 'Da contattare',
      services: []
    });
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsBlacklistModalOpen(true);
  };
  const handleCallback = (item: any) => {
    setSelectedItem(item);
    setFormData({
      ...item,
      callbackDate: item.callbackDate || '',
      callbackTime: item.callbackTime || '',
      callbackNote: item.callbackNote || ''
    });
    setIsCallbackModalOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    // Update local state to reflect changes immediately
    if (selectedItem) {
      setLeadsData(prev => prev.map(item => item.id === selectedItem.id ? formData : item));
    } else {
      // Create new
      const newItem = {
        ...formData,
        id: String(leadsData.length + 1),
        lastContact: new Date().toISOString().split('T')[0]
      };
      setLeadsData([...leadsData, newItem]);
    }
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  const handleCallbackSave = () => {
    setCallbackSaveSuccess(true);
    // Update local state to reflect callback changes
    if (selectedItem) {
      setLeadsData(prev => prev.map(item => item.id === selectedItem.id ? {
        ...item,
        callbackDate: formData.callbackDate,
        callbackTime: formData.callbackTime,
        callbackNote: formData.callbackNote
      } : item));
    }
    setTimeout(() => {
      setCallbackSaveSuccess(false);
      setIsCallbackModalOpen(false);
    }, 1500);
  };
  const confirmBlacklist = () => {
    addNotification('success', 'Lead eliminato', `Lead ${selectedItem.name} aggiunto alla blacklist.`);
    setIsBlacklistModalOpen(false);
  };
  const handleUpload = () => {
    setTimeout(() => {
      addNotification('success', 'Importazione completata', '5 lead importati con successo. 1 duplicato ignorato.');
      setIsUploadModalOpen(false);
    }, 1500);
  };
  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, card: any) => {
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    if (draggedCard && draggedCard.status !== targetStatus) {
      setLeadsData(prev => prev.map(item => item.id === draggedCard.id ? {
        ...item,
        status: targetStatus
      } : item));
      addNotification('info', 'Stato aggiornato', `Lead spostato in "${targetStatus}"`);
    }
    setDraggedCard(null);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-2559">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-2560">
        <div data-id="element-2561">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2562">Commerciale</h1>
          <p className="text-slate-500 mt-1" data-id="element-2563">
            Gestione pipeline, lead e contatti.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-2564">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-2565">
            <button onClick={() => setViewMode('kanban')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'kanban' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2566">
              Pipeline
            </button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2567">
              Lista
            </button>
            <button onClick={() => setViewMode('blacklist')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'blacklist' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2568">
              Blacklist
            </button>
          </div>
          <Button variant="flat" color="secondary" onClick={() => setIsUploadModalOpen(true)} startContent={<Upload className="h-4 w-4" data-id="element-2570" />} data-id="element-2569">
            Importa
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-2572" />} data-id="element-2571">
            Nuovo lead
          </Button>
        </div>
      </div>

      {viewMode !== 'blacklist' && <Card className="mb-6" noPadding data-id="element-2573">
          <div className="p-4 flex flex-col gap-4" data-id="element-2574">
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-2575">
              <div className="flex-1" data-id="element-2576">
                <Input placeholder="Cerca lead..." startContent={<Search className="h-4 w-4" data-id="element-2578" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-2577" />
              </div>
              <div className="flex gap-2" data-id="element-2579">
                <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-2581" />} data-id="element-2580">
                  Filtri{' '}
                  {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-2582">
                      {Object.keys(filters).filter(k => filters[k]).length}
                    </Badge>}
                </Button>
                {viewMode === 'list' && <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="commercial" data-id="element-2583" />}
              </div>
            </div>

            {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-2584">
                <Select label="Provenienza" value={filters.source || ''} onChange={e => setFilter('source', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Web',
            label: 'Web'
          }, {
            value: 'Fiera',
            label: 'Fiera'
          }, {
            value: 'Passaparola',
            label: 'Passaparola'
          }, {
            value: 'Altro',
            label: 'Altro'
          }]} data-id="element-2585" />
                <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Da contattare',
            label: 'Da contattare'
          }, {
            value: 'Da ricontattare',
            label: 'Da ricontattare'
          }, {
            value: 'In lavorazione',
            label: 'In lavorazione'
          }, {
            value: 'Positivo',
            label: 'Positivo'
          }, {
            value: 'Negativo',
            label: 'Negativo'
          }]} data-id="element-2586" />
                <Select label="Servizi" value={filters.services || ''} onChange={e => setFilter('services', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Consulenza',
            label: 'Consulenza'
          }, {
            value: 'Formazione',
            label: 'Formazione'
          }, {
            value: 'Altro',
            label: 'Altro'
          }]} data-id="element-2587" />
                <Select label="Assegnato a" value={filters.assignedTo || ''} onChange={e => setFilter('assignedTo', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Alessio',
            label: 'Alessio'
          }, {
            value: 'Lorenzo',
            label: 'Lorenzo'
          }, {
            value: 'Gian Franco',
            label: 'Gian Franco'
          }, {
            value: 'Tiziano',
            label: 'Tiziano'
          }]} data-id="element-2588" />
                <div className="col-span-full flex justify-end" data-id="element-2589">
                  <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-2591" />} data-id="element-2590">
                    Resetta filtri
                  </Button>
                </div>
              </div>}
          </div>
        </Card>}

      {viewMode === 'kanban' && <div className="overflow-x-auto pb-4" data-id="element-2592">
          <div className="flex gap-4 min-w-max" data-id="element-2593">
            {kanbanColumns.map(col => {
          const cards = searchResult.filter(i => i.status === col.id);
          return <div key={col.id} className={`w-72 flex-shrink-0 rounded-xl border ${col.color} flex flex-col max-h-[calc(100vh-280px)]`} onDragOver={handleDragOver} onDrop={e => handleDrop(e, col.id)} data-id="element-2594">
                  <div className="p-3 border-b border-slate-200/50 flex items-center justify-between" data-id="element-2595">
                    <h3 className="font-semibold text-slate-700 text-sm" data-id="element-2596">
                      {col.title}
                    </h3>
                    <Badge variant="neutral" size="sm" data-id="element-2597">
                      {cards.length}
                    </Badge>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2" data-id="element-2598">
                    {cards.map(item => <div key={item.id} draggable onDragStart={e => handleDragStart(e, item)} onClick={() => handleView(item)} className={`bg-white p-3 rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer ${draggedCard?.id === item.id ? 'opacity-50' : ''}`} data-id="element-2599">
                        <div className="flex justify-between items-start mb-2" data-id="element-2600">
                          <span className="font-bold text-seform-navy text-sm line-clamp-1" data-id="element-2601">
                            {item.name}
                          </span>
                          <Badge variant="neutral" size="sm" className="text-[10px] flex-shrink-0 ml-2" data-id="element-2602">
                            {item.source}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-2603">
                          <User className="h-3 w-3" data-id="element-2604" /> {item.contact}
                        </p>
                        <p className="text-xs text-slate-500 mb-2 flex items-center gap-1" data-id="element-2605">
                          <PhoneCall className="h-3 w-3" data-id="element-2606" /> {item.phone}
                        </p>
                        {item.status === 'Da ricontattare' && item.callbackDate && <div className="mb-2 p-1.5 bg-yellow-50 rounded border border-yellow-100 flex items-center gap-1.5" data-id="element-2607">
                              <CalendarClock className="h-3 w-3 text-yellow-600" data-id="element-2608" />
                              <span className="text-[10px] text-yellow-700 font-medium" data-id="element-2609">
                                {item.callbackDate} {item.callbackTime}
                              </span>
                            </div>}
                        {item.status === 'In lavorazione' && item.appointmentDate && <div className="mb-2 p-1.5 bg-blue-50 rounded border border-blue-100 flex items-center gap-1.5" data-id="element-2610">
                              <Calendar className="h-3 w-3 text-blue-600" data-id="element-2611" />
                              <span className="text-[10px] text-blue-700 font-medium" data-id="element-2612">
                                {item.appointmentDate} {item.appointmentTime}
                              </span>
                            </div>}
                        <div className="flex justify-between items-center pt-2 border-t border-slate-50" data-id="element-2613">
                          <span className="text-[10px] text-slate-400" data-id="element-2614">
                            {item.lastContact}
                          </span>
                          <div className="w-6 h-6 rounded-full bg-seform-orange/10 text-seform-orange text-xs flex items-center justify-center font-bold" data-id="element-2615">
                            {item.assignedTo.charAt(0)}
                          </div>
                        </div>
                      </div>)}
                    {cards.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-2616">
                        Nessun elemento
                      </div>}
                  </div>
                </div>;
        })}
          </div>
        </div>}

      {viewMode === 'list' && <Card noPadding className="overflow-hidden" data-id="element-2617">
          <div className="overflow-x-auto" data-id="element-2618">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2619">
              <thead data-id="element-2620">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2621">
                  {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-2622">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2623">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2624">
                    {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2625">
                        {item.name}
                      </td>}
                    {visibleColumns.includes('contact') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2626">
                        {item.contact}
                      </td>}
                    {visibleColumns.includes('phone') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2627">
                        {item.phone}
                      </td>}
                    {visibleColumns.includes('source') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2628">
                        {item.source}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2629">
                        <Badge variant={item.status === 'Positivo' ? 'success' : item.status === 'Negativo' ? 'danger' : 'info'} data-id="element-2630">
                          {item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('lastContact') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2631">
                        {item.lastContact}
                      </td>}
                    {visibleColumns.includes('assignedTo') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2632">
                        {item.assignedTo}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2633">
                        <div className="flex items-center gap-1" data-id="element-2634">
                          <Tooltip content="Vedi dettaglio" data-id="element-2635">
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleView(item)} data-id="element-2636">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-2637" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Modifica" data-id="element-2638">
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-2639">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-2640" />
                            </Button>
                          </Tooltip>
                          {item.status === 'Da ricontattare' && <Tooltip content="Programma ricontatto" data-id="element-2641">
                              <Button variant="light" size="sm" className="!px-2" onClick={() => handleCallback(item)} data-id="element-2642">
                                <CalendarClock className="h-4 w-4 text-blue-500" data-id="element-2643" />
                              </Button>
                            </Tooltip>}
                          <Tooltip content="Elimina" data-id="element-2644">
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleDelete(item)} data-id="element-2645">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-2646" />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2647">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2648" />
          </div>
        </Card>}

      {viewMode === 'blacklist' && <Card noPadding className="overflow-hidden" data-id="element-2649">
          <div className="p-4 bg-red-50 border-b border-red-100 flex items-center gap-2 text-red-700 text-sm" data-id="element-2650">
            <Ban className="h-4 w-4" data-id="element-2651" />I contatti in questa lista sono bloccati
            e non possono essere re-importati o creati manualmente.
          </div>
          <div className="overflow-x-auto" data-id="element-2652">
            <table className="w-full text-left border-collapse" data-id="element-2653">
              <thead data-id="element-2654">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2655">
                  <th className="px-6 py-4" data-id="element-2656">Ragione sociale</th>
                  <th className="px-6 py-4" data-id="element-2657">Telefono</th>
                  <th className="px-6 py-4" data-id="element-2658">Motivazione</th>
                  <th className="px-6 py-4" data-id="element-2659">Data eliminazione</th>
                  <th className="px-6 py-4" data-id="element-2660">Eliminato da</th>
                  <th className="px-6 py-4 text-right" data-id="element-2661">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2662">
                {blacklistData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors" data-id="element-2663">
                    <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2664">
                      {item.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2665">
                      {item.phone}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2666">
                      {item.reason}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2667">
                      {item.date}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2668">
                      {item.by}
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2669">
                      <Tooltip content="Ripristina lead" data-id="element-2670">
                        <Button variant="light" color="success" size="sm" data-id="element-2671">
                          Ripristina
                        </Button>
                      </Tooltip>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio lead" size="lg" data-id="element-2672">
        {selectedItem && <div className="space-y-6" data-id="element-2673">
            <div className="grid grid-cols-2 gap-4" data-id="element-2674">
              <div data-id="element-2675">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2676">
                  Ragione sociale
                </span>
                <p className="font-medium" data-id="element-2677">{selectedItem.name}</p>
              </div>
              <div data-id="element-2678">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2679">
                  Stato
                </span>
                <div className="mt-1" data-id="element-2680">
                  <Badge variant={selectedItem.status === 'Positivo' ? 'success' : selectedItem.status === 'Negativo' ? 'danger' : 'info'} data-id="element-2681">
                    {selectedItem.status}
                  </Badge>
                </div>
              </div>
              <div data-id="element-2682">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2683">
                  Referente
                </span>
                <p className="font-medium" data-id="element-2684">{selectedItem.contact}</p>
              </div>
              <div data-id="element-2685">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2686">
                  Telefono
                </span>
                <p className="font-medium" data-id="element-2687">{selectedItem.phone}</p>
              </div>
              <div data-id="element-2688">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2689">
                  Email
                </span>
                <p className="font-medium" data-id="element-2690">{selectedItem.email}</p>
              </div>
              <div data-id="element-2691">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2692">
                  Provenienza
                </span>
                <p className="font-medium" data-id="element-2693">{selectedItem.source}</p>
              </div>
              <div data-id="element-2694">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2695">
                  Assegnato a
                </span>
                <p className="font-medium" data-id="element-2696">{selectedItem.assignedTo}</p>
              </div>
              <div data-id="element-2697">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2698">
                  Ultimo contatto
                </span>
                <p className="font-medium" data-id="element-2699">{selectedItem.lastContact}</p>
              </div>
            </div>

            {selectedItem.status === 'In lavorazione' && <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mt-4" data-id="element-2700">
                <h4 className="text-sm font-bold text-blue-800 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-2701">
                  <Calendar className="h-4 w-4" data-id="element-2702" /> Prossimo appuntamento
                </h4>
                <div className="grid grid-cols-2 gap-4 mb-3" data-id="element-2703">
                  <div data-id="element-2704">
                    <span className="text-xs font-semibold text-blue-400 uppercase" data-id="element-2705">
                      Data
                    </span>
                    <p className="font-medium text-blue-900" data-id="element-2706">
                      {selectedItem.appointmentDate || '-'}
                    </p>
                  </div>
                  <div data-id="element-2707">
                    <span className="text-xs font-semibold text-blue-400 uppercase" data-id="element-2708">
                      Ora
                    </span>
                    <p className="font-medium text-blue-900" data-id="element-2709">
                      {selectedItem.appointmentTime || '-'}
                    </p>
                  </div>
                </div>
                <div data-id="element-2710">
                  <span className="text-xs font-semibold text-blue-400 uppercase" data-id="element-2711">
                    Note appuntamento
                  </span>
                  <p className="text-sm text-blue-800 mt-1" data-id="element-2712">
                    {selectedItem.appointmentNote || '-'}
                  </p>
                </div>
                <div className="mt-3 flex justify-end" data-id="element-2713">
                  <Button size="sm" variant="flat" color="primary" onClick={() => {
              setIsViewModalOpen(false);
              handleEdit(selectedItem);
            }} data-id="element-2714">
                    Modifica appuntamento
                  </Button>
                </div>
              </div>}

            {selectedItem.notes && <div data-id="element-2715">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2716">
                  Note
                </span>
                <p className="text-sm text-slate-600 mt-1" data-id="element-2717">
                  {selectedItem.notes}
                </p>
              </div>}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2718">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-2719">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-2721" />} data-id="element-2720">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica lead' : 'Nuovo lead'} size="2xl" data-id="element-2722">
        <AnimatePresence mode="wait" data-id="element-2723">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2724">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2725">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2726" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2727">
                Lead salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2728">
              <div className="space-y-8" data-id="element-2729">
                <section data-id="element-2730">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2731">
                    <User className="h-4 w-4" data-id="element-2732" /> Dati lead
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2733">
                    <Input label="Ragione sociale" placeholder="Azienda SPA" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required data-id="element-2734" />
                    <Input label="Referente" placeholder="Nome Cognome" value={formData.contact || ''} onChange={e => setFormData({
                  ...formData,
                  contact: e.target.value
                })} required data-id="element-2735" />
                    <Input label="Telefono" type="tel" value={formData.phone || ''} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} required data-id="element-2736" />
                    <Input label="Email" type="email" value={formData.email || ''} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} data-id="element-2737" />
                    <Select label="Provenienza" value={formData.source || ''} onChange={e => setFormData({
                  ...formData,
                  source: e.target.value
                })} options={[{
                  value: 'Web',
                  label: 'Web'
                }, {
                  value: 'Fiera',
                  label: 'Fiera'
                }, {
                  value: 'Passaparola',
                  label: 'Passaparola'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} required data-id="element-2738" />
                    <Select label="Stato" value={formData.status || 'Da contattare'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Da contattare',
                  label: 'Da contattare'
                }, {
                  value: 'Da ricontattare',
                  label: 'Da ricontattare'
                }, {
                  value: 'In lavorazione',
                  label: 'In lavorazione'
                }, {
                  value: 'Positivo',
                  label: 'Positivo'
                }, {
                  value: 'Negativo',
                  label: 'Negativo'
                }]} required data-id="element-2739" />
                    <Select label="Assegnato a" value={formData.assignedTo || ''} onChange={e => setFormData({
                  ...formData,
                  assignedTo: e.target.value
                })} options={[{
                  value: 'Alessio',
                  label: 'Alessio'
                }, {
                  value: 'Lorenzo',
                  label: 'Lorenzo'
                }, {
                  value: 'Gian Franco',
                  label: 'Gian Franco'
                }, {
                  value: 'Tiziano',
                  label: 'Tiziano'
                }]} required data-id="element-2740" />
                    <div className="col-span-full" data-id="element-2741">
                      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2742">
                        Servizi di interesse
                      </label>
                      <div className="grid grid-cols-2 gap-3" data-id="element-2743">
                        {[{
                      value: 'consulenza',
                      label: 'Consulenza'
                    }, {
                      value: 'formazione',
                      label: 'Formazione'
                    }, {
                      value: 'altro',
                      label: 'Altro'
                    }].map(service => <label key={service.value} className="flex items-center gap-2 cursor-pointer" data-id="element-2744">
                            <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={(formData.services || []).includes(service.value)} onChange={e => {
                        const current = formData.services || [];
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            services: [...current, service.value]
                          });
                        } else {
                          setFormData({
                            ...formData,
                            services: current.filter((s: string) => s !== service.value)
                          });
                        }
                      }} data-id="element-2745" />
                            <span className="text-sm text-slate-700" data-id="element-2746">
                              {service.label}
                            </span>
                          </label>)}
                      </div>
                    </div>
                    <div className="col-span-full" data-id="element-2747">
                      <Textarea label="Note" value={formData.notes || ''} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} data-id="element-2748" />
                    </div>
                  </div>
                </section>

                {/* Conditional Fields based on Status */}
                {formData.status === 'Da ricontattare' && <>
                    <div className="h-px bg-slate-100" data-id="element-2749"></div>
                    <section data-id="element-2750">
                      <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2751">
                        <CalendarClock className="h-4 w-4" data-id="element-2752" /> Programma
                        ricontatto
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2753">
                        <DatePicker label="Data ricontatto" value={formData.callbackDate || ''} onChange={e => setFormData({
                    ...formData,
                    callbackDate: e.target.value
                  })} required data-id="element-2754" />
                        <Input label="Ora" type="time" value={formData.callbackTime || ''} onChange={e => setFormData({
                    ...formData,
                    callbackTime: e.target.value
                  })} required data-id="element-2755" />
                        <div className="col-span-full" data-id="element-2756">
                          <Textarea label="Nota per ricontatto" value={formData.callbackNote || ''} onChange={e => setFormData({
                      ...formData,
                      callbackNote: e.target.value
                    })} data-id="element-2757" />
                        </div>
                      </div>
                    </section>
                  </>}

                {formData.status === 'In lavorazione' && <>
                    <div className="h-px bg-slate-100" data-id="element-2758"></div>
                    <section data-id="element-2759">
                      <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2760">
                        <Calendar className="h-4 w-4" data-id="element-2761" /> Prossimo appuntamento
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2762">
                        <DatePicker label="Data appuntamento" value={formData.appointmentDate || ''} onChange={e => setFormData({
                    ...formData,
                    appointmentDate: e.target.value
                  })} required data-id="element-2763" />
                        <Input label="Ora" type="time" value={formData.appointmentTime || ''} onChange={e => setFormData({
                    ...formData,
                    appointmentTime: e.target.value
                  })} required data-id="element-2764" />
                        <div className="col-span-full" data-id="element-2765">
                          <Textarea label="Note appuntamento" value={formData.appointmentNote || ''} onChange={e => setFormData({
                      ...formData,
                      appointmentNote: e.target.value
                    })} data-id="element-2766" />
                        </div>
                      </div>
                    </section>
                  </>}

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2767">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-2768">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2770" />} data-id="element-2769">
                    Salva lead
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Callback Modal */}
      <Modal isOpen={isCallbackModalOpen} onClose={() => setIsCallbackModalOpen(false)} title="Programma ricontatto" size="md" data-id="element-2771">
        <AnimatePresence mode="wait" data-id="element-2772">
          {callbackSaveSuccess ? <motion.div key="callback-success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2773">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2774">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2775" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2776">
                Ricontatto programmato con successo
              </p>
            </motion.div> : <motion.div key="callback-form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2777">
              <div className="space-y-6" data-id="element-2778">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2779">
                  <DatePicker label="Data ricontatto" value={formData.callbackDate || ''} onChange={e => setFormData({
                ...formData,
                callbackDate: e.target.value
              })} required data-id="element-2780" />
                  <Input label="Ora" type="time" value={formData.callbackTime || ''} onChange={e => setFormData({
                ...formData,
                callbackTime: e.target.value
              })} required data-id="element-2781" />
                  <div className="col-span-full" data-id="element-2782">
                    <Textarea label="Nota per ricontatto" value={formData.callbackNote || ''} onChange={e => setFormData({
                  ...formData,
                  callbackNote: e.target.value
                })} placeholder="Motivo del ricontatto..." data-id="element-2783" />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2784">
                  <Button variant="flat" color="default" onClick={() => setIsCallbackModalOpen(false)} data-id="element-2785">
                    Annulla
                  </Button>
                  <Button onClick={handleCallbackSave} startContent={<Save className="h-4 w-4" data-id="element-2787" />} data-id="element-2786">
                    Salva
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Upload Modal */}
      <Modal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} title="Importa lead da Excel" size="lg" data-id="element-2788">
        <div className="space-y-6" data-id="element-2789">
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:bg-slate-50 transition-colors cursor-pointer" data-id="element-2790">
            <Upload className="h-10 w-10 text-slate-400 mx-auto mb-4" data-id="element-2791" />
            <p className="text-sm text-slate-600 font-medium" data-id="element-2792">
              Trascina qui il file Excel o clicca per selezionare
            </p>
            <p className="text-xs text-slate-400 mt-2" data-id="element-2793">
              Formati supportati: .xlsx, .csv
            </p>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-2794">
            <h5 className="text-sm font-bold text-blue-800 mb-2" data-id="element-2795">
              Campi richiesti nel file:
            </h5>
            <ul className="list-disc list-inside text-xs text-blue-700 space-y-1" data-id="element-2796">
              <li data-id="element-2797">Ragione sociale</li>
              <li data-id="element-2798">Telefono</li>
              <li data-id="element-2799">Referente</li>
              <li data-id="element-2800">Provenienza</li>
            </ul>
            <div className="mt-3 text-xs text-blue-600" data-id="element-2801">
              <a href="#" className="underline" data-id="element-2802">
                Scarica template di esempio
              </a>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4" data-id="element-2803">
            <Button variant="flat" color="default" onClick={() => setIsUploadModalOpen(false)} data-id="element-2804">
              Annulla
            </Button>
            <Button onClick={handleUpload} data-id="element-2805">Importa file</Button>
          </div>
        </div>
      </Modal>

      {/* Blacklist Modal */}
      <Modal isOpen={isBlacklistModalOpen} onClose={() => setIsBlacklistModalOpen(false)} title="Elimina e aggiungi a blacklist" size="md" data-id="element-2806">
        <div className="space-y-6" data-id="element-2807">
          <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-2808">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-2809" />
            <p className="text-sm text-red-700" data-id="element-2810">
              Attenzione: il lead verrà rimosso dalla pipeline e aggiunto alla
              blacklist. Non sarà più possibile importarlo o crearlo manualmente
              con questo numero di telefono.
            </p>
          </div>

          <Select label="Motivazione eliminazione" options={[{
          value: 'not_interested',
          label: 'Non interessato'
        }, {
          value: 'no_answer',
          label: 'Non ha mai risposto'
        }, {
          value: 'unreachable',
          label: 'Impossibile parlare con referente'
        }, {
          value: 'other',
          label: 'Altro'
        }]} required data-id="element-2811" />

          <Textarea label="Note aggiuntive" placeholder="Dettagli..." data-id="element-2812" />

          <div className="flex justify-end gap-3 pt-4" data-id="element-2813">
            <Button variant="flat" color="default" onClick={() => setIsBlacklistModalOpen(false)} data-id="element-2814">
              Annulla
            </Button>
            <Button color="danger" onClick={confirmBlacklist} data-id="element-2815">
              Conferma eliminazione
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>;
}